/**
 * Chemins toujours accessibles a un utilisateur authentifie : ils n'ont
 * volontairement aucune entree de menu.
 */
export const CHEMINS_LIBRES = [
  '/mon-profil',
  '/dashboard',
  '/application-mobile',
];

const correspond = (pathname, path) =>
  !!path && (pathname === path || pathname.startsWith(path + '/'));

/** Tous les chemins du menu, parents et enfants confondus. */
const cheminsDuMenu = (menu) =>
  menu.flatMap((item) => [item.path, ...(item.children ?? []).map((c) => c.path)])
    .filter(Boolean);

/**
 * Vrai si pathname est couvert par une entree du menu ou par un chemin libre.
 * menu null/undefined => on autorise : le payload n'est pas encore arrive.
 *
 * `catalogue` = tous les chemins declares dans `entree_menu`, tous droits
 * confondus (/v1/navigation/chemins-declares).
 *
 * POURQUOI LE CATALOGUE. La regle « le chemin commence par un chemin de mon
 * menu » est indispensable aux routes de detail : `/sinistres/<id>` n'est
 * declare nulle part, il n'est atteignable que via `/sinistres`. Mais elle
 * ouvrait du meme coup les ecrans FRERES qui ont leur propre habilitation -
 * `/sinistres/affectations-en-attente` (SINISTRE_AUTO_AFFECTATION) et `/sinistres/nouveau`
 * (SINISTRE_CREATE) etaient atteignables en tapant l'URL par tout profil ayant
 * SINISTRE_READ, COMPTABLE et GESTIONNAIRE compris.
 *
 * D'ou l'arbitrage : c'est le chemin declare le PLUS SPECIFIQUE couvrant l'URL
 * qui decide. S'il est dans le menu de l'utilisateur, on passe ; sinon on
 * refuse, meme si un parent moins specifique, lui, y est. Une URL que le
 * catalogue ne connait pas du tout reste arbitree par le prefixe : c'est le cas
 * des routes de detail, et c'est ce qui les preserve.
 */
export const cheminAutorise = (menu, pathname, catalogue = null) => {
  if (menu === null || menu === undefined) return true;
  if (CHEMINS_LIBRES.some((libre) => correspond(pathname, libre))) return true;

  const duMenu = cheminsDuMenu(menu);
  const parPrefixe = () => duMenu.some((path) => correspond(pathname, path));

  // Catalogue absent (backend plus ancien, ou appel en echec) : on retombe sur
  // l'ancienne regle. Trop permissive, mais elle ne bloque personne - et la
  // frontiere qui compte reste @PreAuthorize cote serveur.
  if (!Array.isArray(catalogue) || catalogue.length === 0) return parPrefixe();

  const declaresCouvrants = catalogue
    .filter((path) => correspond(pathname, path))
    .sort((a, b) => b.length - a.length);

  // Hors catalogue : route de detail ou ecran sans entree de menu.
  if (declaresCouvrants.length === 0) return parPrefixe();

  return duMenu.includes(declaresCouvrants[0]);
};

/**
 * Decision d'acces a une route. Deux etages, dans cet ordre.
 *
 * 1. La route DECLARE son habilitation : elle tranche seule, sur les seules
 *    habilitations de l'utilisateur.
 * 2. Sinon : le catalogue ci-dessus gouverne, par chemin declare le plus
 *    specifique, avec repli sur le prefixe.
 *
 * Le catalogue suffirait pour les ecrans declares en base, et c'est lui qui doit
 * gouverner le cas general - il couvre d'office tout ecran ajoute au menu, sans
 * qu'on ait a y penser. Le premier etage n'est pas un doublon pour autant : il
 * couvre la fenetre ou ni le menu ni le catalogue ne sont encore en cache, que
 * `cheminAutorise` traverse en laissant tout passer pour ne pas bloquer un
 * rechargement de page. Les habilitations, elles, sont connues des la session
 * restauree - une route qui a declare son droit n'a aucune raison d'heriter de
 * cette tolerance, et reste fermee pendant ce delai.
 *
 * A reserver donc aux quelques routes ou l'ouverture transitoire n'est pas
 * acceptable ; partout ailleurs, ne rien declarer et laisser le catalogue faire.
 */
export const accesAutorise = ({
  menu, pathname, catalogue = null, habilitationRequise, habilitations = [],
}) => {
  if (habilitationRequise) return habilitations.includes(habilitationRequise);
  return cheminAutorise(menu, pathname, catalogue);
};
